import { addElementInArray } from '@/lib/array-helpers';
import { pdkAxios } from '@/lib/pdk-axios';
import { VehicleImage } from '@/models/registry';
import type { VehicleImage as VehicleImageType } from '@/models/types';
import {
  makeAutoObservable,
  observable,
  ObservableMap,
  runInAction,
} from 'mobx';
import { db } from '../db';
import type { DeltaPacketRecord } from '../hydrate';
import type { BaseStore } from './base';

/**
 * Unlike other stores that get filled by firing hydration at the beginning and loading in all data from database,
 * this store is intended to be filled lazily, meaning: no initial hydration, only when user visit specific VehicleListing
 * page, then we load images for that Listing specifically.
 * This should also help in reducing initial bootrstrap time of the application.
 *
 * To load images in this way call `VehicleImageStore.loadImagesForListing()`.
 */
class VehicleImageStore implements BaseStore<VehicleImage, VehicleImageType> {
  store: ObservableMap<string, VehicleImage> = observable.map();
  // vehicle_listing_id -> array of vehicle images
  storeIndexedByForeignKey: ObservableMap<string, VehicleImage[]> =
    observable.map();

  constructor() {
    makeAutoObservable(this);
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<VehicleImageType>[];
    hydrateIndexedDB?: boolean;
  }): void {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<VehicleImageType>;
    hydrateIndexedDB?: boolean;
  }): void {
    if (delta.action === 'delete') {
      if (hydrateIndexedDB) {
        db.vehicle_listing_images.delete(delta.record_id);
      }

      this.remove(delta.record_id);
      return;
    }

    // Unsupported delta packet actions
    if (delta.action === 'archive' || delta.action === 'unarchive') {
      return;
    }

    if (hydrateIndexedDB) {
      db.vehicle_listing_images.put(delta.data, delta.record_id);
    }

    this.upsert(delta.data);
  }

  getById(id: string): VehicleImage | undefined {
    return this.store.get(id);
  }

  async loadImagesForListing(
    listingID: string,
    forceReload?: boolean,
  ): Promise<VehicleImage[]> {
    try {
      await this._hydrateFromIndexDB(listingID);

      const vehicleImages = this.storeIndexedByForeignKey.get(listingID);
      if (vehicleImages && !forceReload) {
        return vehicleImages;
      }

      const response = await pdkAxios.get<VehicleImage[]>(
        `/hydrate/v2/single-entity/listing/${listingID}/images`,
      );

      if (response.status >= 400) {
        return Promise.reject(response.data);
      }

      // Images returned from the server have ID that is number, so we have to string here
      const images = response.data.map((img) => {
        img.id = `${img.id}`;
        img.vehicle_listing_id = `${img.vehicle_listing_id}`;
        return img;
      });

      // Save images in store before returning response data
      for (const img of images) {
        this.store.set(img.id, img);
        db.vehicle_listing_images.add(img, img.id);
      }
      this.storeIndexedByForeignKey.set(listingID, response.data);

      return response.data;
    } catch (err) {
      return Promise.reject(err);
    }
  }

  moveImage(
    vehicleListingID: string,
    sourceImageID: string,
    targetImageID: string,
  ): [VehicleImage[], Promise<void>] {
    let imagesCopy = this.getVehicleImages(vehicleListingID);
    const sourceImageIndex = imagesCopy.findIndex(
      (img) => img.id == sourceImageID,
    );
    const targetImageIndex = imagesCopy.findIndex(
      (img) => img.id == targetImageID,
    );

    // Replicate image drag&drop action on the image array
    const sourceImage = imagesCopy[sourceImageIndex];
    imagesCopy = imagesCopy.filter((_, index) => index != sourceImageIndex);
    imagesCopy = addElementInArray(imagesCopy, sourceImage, targetImageIndex);
    imagesCopy = imagesCopy.map((img, index) => {
      // Since we pass `order_index` to `index` parameter of `useSortable` from `dnd-kit/react/sortable` lib,
      // we need to make sure the value start at 1 and not 0 in order to avoid weird unexpected behaviour from the lib.
      img.order_index = index + 1;
      return img;
    });

    let imagesThatNeedPositionUpdate: VehicleImage[] = [];
    // Because array can be only sliced from lower to higher index we have to take into account on how
    // image was dragged: "was image moved from lower position to higher?", in that case source has lower index
    // and target have higher index, which we can use as parameter of `.slice()` function (and vice-versa).
    //
    // You can also noticed we addded +1 to the end of slice position, this is because we want to include all
    // images whose position was affected by this image move/drag.
    if (targetImageIndex > sourceImageIndex) {
      imagesThatNeedPositionUpdate = imagesCopy.slice(
        sourceImageIndex,
        targetImageIndex + 1,
      );
    } else {
      imagesThatNeedPositionUpdate = imagesCopy.slice(
        targetImageIndex,
        sourceImageIndex + 1,
      );
    }

    // Dev note: the reason we include only affected images as part of the payload is to reduce the amount
    // of updates DB would need to do.
    const payload = imagesThatNeedPositionUpdate.map((image) => ({
      image_id: +image.id,
      order_index: image.order_index,
    }));

    const request = new Promise<void>(async (resolve, reject) => {
      try {
        const response = await pdkAxios.put(
          `/inventory/listing/images/re-order`,
          payload,
        );
        if (response.status >= 400) {
          throw Error(response.data);
        }

        // Now that we successfully updated the positions, we can alter in-memory store and IndexDB store.
        for (const image of imagesCopy) {
          this.store.set(image.id, image);
        }
        this.storeIndexedByForeignKey.set(vehicleListingID, imagesCopy);
        db.vehicle_listing_images.bulkUpdate(
          imagesCopy.map((img) => ({
            key: img.id,
            changes: {
              order_index: img.order_index,
            },
          })),
        );

        resolve();
      } catch (error) {
        reject(error);
      }
    });

    return [imagesCopy, request];
  }

  deleteImage(
    vehicleListingID: string,
    imageID: string,
  ): [VehicleImage[], Promise<void>] {
    let imagesCopy = this.getVehicleImages(vehicleListingID);
    const deletedImage = imagesCopy.find((img) => img.id == imageID);
    imagesCopy = imagesCopy.filter((img) => img.id != imageID);

    const request = new Promise<void>(async (resolve, reject) => {
      try {
        const response = await pdkAxios.delete(
          `/inventory/listing/images/${imageID}`,
        );
        if (response.status >= 400) {
          throw Error(response.data);
        }

        // Update in-memory store and IndexDB
        if (deletedImage) {
          this.store.delete(imageID);
          this.storeIndexedByForeignKey.set(vehicleListingID, imagesCopy);
          db.vehicle_listing_images.where('id').equals(imageID);
        }

        resolve();
      } catch (error) {
        reject(error);
      }
    });

    return [imagesCopy, request];
  }

  markVehicleImageAsFeatured(
    vehicleListingID: string,
    imageID: string,
  ): [VehicleImage[], Promise<void>] {
    let imagesCopy = this.getVehicleImages(vehicleListingID);
    const oldFeaturedImage = imagesCopy.find((img) => img.featured);
    const newFeaturedImage = imagesCopy.find((img) => img.id === imageID);
    imagesCopy = imagesCopy.map((img) => {
      img.featured = img.id == imageID;
      return img;
    });

    const request = new Promise<void>(async (resolve, reject) => {
      try {
        const response = await pdkAxios.put(
          `/inventory/listing/images/${imageID}/featured`,
        );

        if (response.status >= 400) {
          throw Error(response.data);
        }

        if (oldFeaturedImage && newFeaturedImage) {
          this.store.set(oldFeaturedImage.id, oldFeaturedImage);
          this.store.set(newFeaturedImage.id, newFeaturedImage);
          this.storeIndexedByForeignKey.set(vehicleListingID, imagesCopy);

          db.vehicle_listing_images.bulkUpdate([
            {
              key: oldFeaturedImage.id,
              changes: {
                featured: oldFeaturedImage.featured,
              },
            },
            {
              key: newFeaturedImage.id,
              changes: {
                featured: newFeaturedImage.featured,
              },
            },
          ]);
        }
        resolve();
      } catch (error) {
        reject(error);
      }
    });

    return [imagesCopy, request];
  }

  async _hydrateFromIndexDB(listingID: string) {
    const indexDBImages = await db.vehicle_listing_images
      .where('vehicle_listing_id')
      .equals(+listingID)
      .toArray();
    const clientModelImages: VehicleImage[] = [];

    // Nothing to hydrate
    if (indexDBImages.length === 0) {
      return;
    }

    runInAction(() => {
      for (const img of indexDBImages) {
        const clientModelImage = new VehicleImage(img);
        this.store.set(img.id, clientModelImage);
        clientModelImages.push(clientModelImage);
      }

      this.storeIndexedByForeignKey.set(listingID, clientModelImages);
    });
  }

  add(input: VehicleImageType): void {
    const vehicleImage = new VehicleImage(input);
    this.store.set(vehicleImage.id, vehicleImage);

    // Make sure VehicleImage is indexed by `vehicle_image_id` foreign key for easier association.
    const images =
      this.storeIndexedByForeignKey.get(vehicleImage.vehicle_listing_id) || [];
    images.push(vehicleImage);
    this.storeIndexedByForeignKey.set(vehicleImage.vehicle_listing_id, images);
  }

  save(vehicleImage: VehicleImage): void {
    this.store.set(vehicleImage.id, vehicleImage);

    // Make sure VehicleImage is indexed by `vehicle_image_id` foreign key for easier association.
    const images =
      this.storeIndexedByForeignKey.get(vehicleImage.vehicle_listing_id) || [];
    const index = images.findIndex((img) => img.id === vehicleImage.id);
    if (index !== -1) {
      images[index] = vehicleImage;
    } else {
      images.push(vehicleImage);
    }
    this.storeIndexedByForeignKey.set(vehicleImage.vehicle_listing_id, images);
  }

  remove(id: string): void {
    const vehicleImge = this.store.get(id);
    if (!vehicleImge) {
      return;
    }

    this.store.delete(id);
    const vehicleListingID = vehicleImge.vehicle_listing_id;
    const listingsVehicleImages =
      this.storeIndexedByForeignKey.get(vehicleListingID) || [];
    this.storeIndexedByForeignKey.set(
      vehicleListingID,
      listingsVehicleImages.filter((n) => n.id !== id),
    );
  }

  replace({
    id,
    vehicleImage,
  }: {
    id: string;
    vehicleImage: VehicleImageType;
  }) {
    const existingImage = this.getById(id);
    if (!existingImage) {
      return;
    }

    existingImage.vehicle_listing_id = vehicleImage.vehicle_listing_id;
    existingImage.image_url = vehicleImage.image_url;
    existingImage.featured = vehicleImage.featured;
    existingImage.blur_hash_img = vehicleImage.blur_hash_img;
    existingImage.status = vehicleImage.status;
    existingImage.order_index = vehicleImage.order_index;
    existingImage.timestamp = vehicleImage.timestamp;

    this.save(vehicleImage);
  }

  upsert(vehicleImage: VehicleImageType) {
    const existingImage = this.getById(vehicleImage.id);
    if (existingImage) {
      this.replace({ id: vehicleImage.id, vehicleImage });
    } else {
      this.add(vehicleImage);
    }
  }

  /**
   * Returns all images from the store and sorts them by their `order_index` ASC.
   * This is important because `order_index` indicates its position both on the UI in admin panel,
   * as well as Vehicle Details Page on product website.
   */
  getVehicleImages(vehicleListingID: string): VehicleImage[] {
    const vehicleImages = this.storeIndexedByForeignKey.get(vehicleListingID);
    if (vehicleImages) {
      return vehicleImages
        .map((img) => ({ ...img }))
        .sort((img1, img2) => {
          return img1.order_index - img2.order_index;
        })
        .map((img, index) => {
          // All images that were uploaded using new architecture should have `order_index` defined, but a lot of older vehicles
          // don't, and we have to account for that by replacing default 0 value to 1.
          //
          // This is important because `order_index` is passed to `index` parameter of `useSortable` from `dnd-kit/react/sortable` lib,
          // we need to make sure the value start at 1 and not 0 in order to avoid weird unexpected behaviour from the lib.
          if (img.order_index <= 0) {
            img.order_index = 1;
          }

          img.order_index = index + 1;
          return img;
        });
    }

    return [];
  }
}

export const vehicleImageStore = new VehicleImageStore();

declare global {
  interface Window {
    vehicleImageStore: VehicleImageStore;
  }
}
